// Titel einer Lead-Liste mit Smart-View-Wechsler (Agent 5B, Runde 5; Close „Leads ▾“): Klick öffnet ein Fenster mit Suche und allen
// Lead-Smart-Views, die zuletzt geöffneten zuerst. Ein Klick auf einen Eintrag wechselt die Liste. Pfeiltasten und Enter gehen auch.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SmartViewKurz } from "../../api";
import { listenAnsichtenApi } from "../../api/listenAnsichten";
import { useAuth } from "../../auth/AuthContext";
import { istTrenner, kurzName, useSmartViews } from "../../daten/smartviews";
import { Ausklapp } from "../Ausklapp";
import { Icon } from "../Icon";
import { feldTrifft, sortierName, textTrifft, zuletztGenutzt } from "./BedienleisteLogik";
import "./Bedienleiste.css";

interface Eintrag {
  id: string; // "leads" oder die Smart-View-ID
  name: string;
  voll: string;
  pfad: string;
  pinned: boolean;
  alleLeads?: boolean;
}

const LEADS_ID = "leads";

interface Props {
  /** Anzeigename der offenen Liste */
  titel: string;
  /** ID der offenen Smart View; "leads" auf der Leads-Liste */
  aktuell: string;
}

export function ListenTitel({ titel, aktuell }: Props) {
  const [offen, setOffen] = useState(false);
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  return (
    <>
      <h1 className="liste-kopf__titel bl-titelzeile">
        <button
          ref={setAnker}
          type="button"
          className="bl-titel"
          aria-haspopup="listbox"
          aria-expanded={offen}
          title="Smart View wechseln"
          onClick={() => setOffen((o) => !o)}
        >
          <span className="bl-titel__text">{titel}</span>
          <Icon name="chevron-unten" groesse={14} />
        </button>
      </h1>
      <SmartViewWechsler anker={anker} offen={offen} onSchliessen={() => setOffen(false)} aktuell={aktuell} />
    </>
  );
}

/** Fenster mit Suche und allen Lead-Smart-Views; auch für die Lupe in der Seitenleiste (`aktuell` = ID der offenen Smart View, sonst "leads"). */
export function SmartViewWechsler({ anker, offen, onSchliessen, aktuell }: { anker: HTMLElement | null; offen: boolean; onSchliessen: () => void; aktuell: string }) {
  const { nutzer } = useAuth();
  const navigate = useNavigate();
  const { views } = useSmartViews();
  const [suche, setSuche] = useState("");
  const [aktiv, setAktiv] = useState(0);
  const [zeiten, setZeiten] = useState<Record<string, string | null>>({});
  const eingabe = useRef<HTMLInputElement>(null);
  const liste = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    setSuche("");
    setAktiv(0);
    window.setTimeout(() => eingabe.current?.focus(), 0);
    let weg = false;
    listenAnsichtenApi
      .alle()
      .then((r) => {
        if (weg) return;
        const z: Record<string, string | null> = {};
        for (const a of r.data) if (a.liste.startsWith("smartview:")) z[a.liste.slice("smartview:".length)] = a.zuletzt_genutzt_at;
        setZeiten(z);
      })
      .catch(() => undefined);
    return () => {
      weg = true;
    };
  }, [offen]);

  const alle = useMemo<Eintrag[]>(() => {
    const leadViews = (views ?? []).filter((v: SmartViewKurz) => (v.type || "lead") === "lead" && !istTrenner(v.name));
    return leadViews
      .map<Eintrag>((v) => ({ id: v.id, name: kurzName(v.name, nutzer?.first_name), voll: v.name, pfad: `/smart-views/${v.id}`, pinned: v.pinned }))
      .sort((a, b) => sortierName(a.voll).localeCompare(sortierName(b.voll), "de", { numeric: true }));
  }, [views, nutzer?.first_name]);

  const leads: Eintrag = { id: LEADS_ID, name: "Alle Leads", voll: "Alle Leads", pfad: "/leads", pinned: false, alleLeads: true };

  // Abschnitte: „Zuletzt“ (nur ohne Suche), danach alle (Reihenfolge wie die Auswahl per Tastatur)
  const abschnitte = useMemo(() => {
    const such = suche.trim();
    if (such) {
      const treffer = alle.filter((e) => feldTrifft({ label: e.voll }, such) || textTrifft(e.name, such));
      const mitLeads = textTrifft("alle leads", such) ? [leads, ...treffer] : treffer;
      return [{ titel: null as string | null, eintraege: mitLeads }];
    }
    const zuletzt = zuletztGenutzt(alle, zeiten, aktuell);
    const ids = new Set(zuletzt.map((e) => e.id));
    const rest = alle.filter((e) => !ids.has(e.id));
    const ergebnis: { titel: string | null; eintraege: Eintrag[] }[] = [{ titel: null, eintraege: aktuell === LEADS_ID ? [] : [leads] }];
    if (zuletzt.length) ergebnis.push({ titel: "Zuletzt geöffnet", eintraege: zuletzt });
    ergebnis.push({ titel: "Smart Views", eintraege: rest });
    return ergebnis.filter((a) => a.eintraege.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alle, suche, zeiten, aktuell]);

  const flach = abschnitte.flatMap((a) => a.eintraege);
  useEffect(() => setAktiv(0), [suche]);
  useEffect(() => {
    liste.current?.querySelector<HTMLElement>(`[data-nr="${aktiv}"]`)?.scrollIntoView({ block: "nearest" });
  }, [aktiv]);

  const waehlen = (e: Eintrag) => {
    onSchliessen();
    if (e.id !== aktuell) navigate(e.pfad);
  };

  let nr = -1;
  return (
    <Ausklapp anker={anker} offen={offen} onSchliessen={onSchliessen} breite={340} maxHoehe={480} rolle="listbox">
      <div
        className="bl-pop"
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setAktiv((a) => Math.min(a + 1, flach.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setAktiv((a) => Math.max(a - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const ziel = flach[aktiv];
            if (ziel) waehlen(ziel);
          }
        }}
      >
        <label className="bl-pop__suche">
          <Icon name="suche" groesse={14} />
          <input ref={eingabe} value={suche} placeholder="Smart View suchen …" aria-label="Smart View suchen" onChange={(e) => setSuche(e.target.value)} />
        </label>
        <div ref={liste} className="bl-pop__liste" role="listbox" aria-label="Smart Views">
          {views === null ? <div className="bl-pop__leer">Wird geladen …</div> : null}
          {views !== null && !flach.length ? <div className="bl-pop__leer">Keine Smart View gefunden.</div> : null}
          {abschnitte.map((a, i) => (
            <div key={a.titel ?? `a${i}`}>
              {a.titel ? <div className="bl-pop__ueberschrift">{a.titel}</div> : null}
              {a.eintraege.map((e) => {
                nr++;
                const mein = nr;
                return (
                  <button
                    key={e.id}
                    type="button"
                    role="option"
                    aria-selected={e.id === aktuell}
                    data-nr={mein}
                    className={`bl-eintrag${mein === aktiv ? " bl-eintrag--aktiv" : ""}`}
                    title={e.voll}
                    onMouseEnter={() => setAktiv(mein)}
                    onClick={() => waehlen(e)}
                  >
                    {e.alleLeads ? <Icon name="leads" groesse={14} /> : <span className="bl-eintrag__haken">{e.id === aktuell ? <Icon name="haken" groesse={14} /> : null}</span>}
                    <span className="bl-eintrag__text">{e.name}</span>
                    {e.pinned ? (
                      <span className="bl-eintrag__rechts" title="In der Seitenleiste angeheftet">
                        <Icon name="stecknadel" groesse={12} />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="bl-wechsler-fuss">
          <button
            type="button"
            className="bl-eintrag"
            onClick={() => {
              onSchliessen();
              navigate("/smart-views/alle");
            }}
          >
            <Icon name="felder" groesse={14} />
            <span className="bl-eintrag__text">Alle Smart Views verwalten</span>
            <span className="bl-eintrag__rechts">{alle.length}</span>
          </button>
        </div>
      </div>
    </Ausklapp>
  );
}
